import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SpaceBetween, Wizard, WizardProps, Button } from '@amzn/awsui-components-core';
import { generatePath, useHistory } from 'react-router-dom';
import Step1ContentTypeAudience from './Step1ContentTypeAudience';
import Step2ContentMetadata from './Step2ContentMetadata';
import Step3ContentDetails from './Step3ContentDetails';
import Step4DuplicateCheck from './Step4DuplicateCheck';
import constants from '../../../../common/constants';
import RouterPaths from '../../../../common/RouterPaths';
import {
  Content,
  ContentAudience,
  ContentMetadata,
  ContentNode,
  ContentSubType,
  ContentType,
  ContentTypeMetadata,
  FeatureIds,
  PublicationSearchResultItem,
  TechDomain,
  WorkflowEventTypes,
} from '@amzn/awscat-pattern-library-service-model';
import { createWorkflowEventWithStatePolling } from '../../../../api/CreateWorkflowEventWithStatePolling';
import Breadcrumbs from '../../../common/Breadcrumbs';
import FlashbarMessageBroker from '../../../../common/FlashbarMessageBroker';
import * as UtilApi from '../../../../api/UtilsApi';
import { DefaultContentSubTypeMap } from '../../../utils/SelectableOptions';
import { DuplicateScanResultItem } from '../../../duplicate-scan-results/DuplicateScanResultsView';
import CancelWizardModal from './CancelWizardModal';
import {
  recordDuplicationScanAction,
  recordWizardStepNavigation,
  initSession,
} from '../../../../monitoring/PreapprovalTelemetry';
import { MplServiceClientFactory } from '../../../../api/MplServiceClientFactory';
import { AppContext } from '../../../../contexts/AppContext';
import { useCurrentUser } from '../../../../contexts/UserContext';
import { convertEntities } from '@amzn/awscat-editor';
import { decodeSearchtextString } from '../../../utils/Search';
import { FeatureFlagContext } from '../../../../contexts/FeatureFlagContext';
import { useIsFeatureEnabled } from '../../../../contexts/EnabledFeaturesContext';

const CreateContentWizard: React.FC = () => {
  const history = useHistory();
  const { userId, setActiveRole } = useCurrentUser();
  const [activeStepIndex, setActiveStepIndex] = useState(0);

  // Initialize a telemetry session for this wizard instance
  useEffect(() => {
    initSession(userId);
  }, [userId]);

  // Step 1 state
  const [contentType, setContentType] = useState<ContentType | undefined>(undefined);
  const [contentAudience, setContentAudience] = useState<ContentAudience | undefined>(undefined);
  const [stepAttempted, setStepAttempted] = useState(false);

  // Step 2 state
  const [mainTechDomain, setMainTechDomain] = useState<string | undefined>(undefined);
  const [techDomains, setTechDomains] = useState<string[]>([]);
  const [awsServices, setAwsServices] = useState<string[]>([]);
  const [workload, setWorkload] = useState<string[]>([]);
  const [rType, setRType] = useState<string | undefined>(undefined);

  // Step 3 state
  const [contentName, setContentName] = useState('');
  const [contentShortName, setContentShortName] = useState('');
  const [description, setDescription] = useState('');
  const [proposal, setProposal] = useState('');
  const [problemSolved, setProblemSolved] = useState('');

  // Inline duplicate check state (title-based, fires as user types in Step 3)
  const [isSearchingDuplicates, setIsSearchingDuplicates] = useState(false);
  const [duplicateContents, setDuplicateContents] = useState<PublicationSearchResultItem[]>([]);
  const [numberOfDuplicatesToShow, setNumberOfDuplicatesToShow] = useState(
    UtilApi.defaultNumberOfDuplicatesToShow,
  );
  const duplicateSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const duplicateSearchRequestIdRef = useRef(0);
  const appContext = useContext(AppContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const isUxRedesignEnabled = featureFlagContext?.isFeatureEnabled('UxRedesign') ?? false;
  const isAutoChangeRoleEnabled = useIsFeatureEnabled(FeatureIds.AutoChangeRoleAfterCreate);
  const mplService = useMemo(() => MplServiceClientFactory.create(), []);

  const executeDuplicationCheck = useCallback(
    (title: string) => {
      setIsSearchingDuplicates(true);
      setNumberOfDuplicatesToShow(UtilApi.defaultNumberOfDuplicatesToShow);
      if (duplicateSearchTimerRef.current) clearTimeout(duplicateSearchTimerRef.current);
      duplicateSearchTimerRef.current = setTimeout(async () => {
        const currentRequestId = ++duplicateSearchRequestIdRef.current;
        try {
          const searchResults = await mplService.searchPublicationsByField(
            decodeSearchtextString(convertEntities(title, appContext?.entities)),
            'name',
          );
          if (currentRequestId === duplicateSearchRequestIdRef.current) {
            setDuplicateContents(searchResults.publications);
          }
        } catch {
          if (currentRequestId === duplicateSearchRequestIdRef.current) {
            setDuplicateContents([]);
          }
        } finally {
          if (currentRequestId === duplicateSearchRequestIdRef.current) {
            setIsSearchingDuplicates(false);
          }
        }
      }, UtilApi.duplicateSearchTimerDelay);
    },
    [appContext?.entities, mplService],
  );

  // Trigger duplicate check whenever contentName changes
  useEffect(() => {
    if (contentName) {
      executeDuplicationCheck(contentName);
    } else {
      setDuplicateContents([]);
      setIsSearchingDuplicates(false);
    }
    return () => {
      if (duplicateSearchTimerRef.current) clearTimeout(duplicateSearchTimerRef.current);
    };
  }, [contentName, executeDuplicationCheck]);

  const handleShowMoreDuplicates = useCallback(() => {
    setNumberOfDuplicatesToShow((prev) => prev + UtilApi.numberOfDuplicatesToAdd);
  }, []);

  const isStep1Valid = contentType !== undefined && contentAudience !== undefined;
  const isStep2Valid =
    mainTechDomain !== undefined &&
    (mainTechDomain !== TechDomain.Migration || rType !== undefined);
  const isStep3Valid =
    contentName.length >= UtilApi.longTitleInputMinLength &&
    contentName.length <= UtilApi.longTitleInputMaxLength &&
    contentShortName.length >= UtilApi.shortTitleInputMinLength &&
    contentShortName.length <= UtilApi.shortTitleInputMaxLength &&
    description.length >= UtilApi.descriptionInputMinLength &&
    description.length <= UtilApi.descriptionInputMaxLength &&
    proposal.length >= UtilApi.proposalInputMinLength &&
    proposal.length <= UtilApi.proposalInputMaxLength &&
    problemSolved.length >= UtilApi.problemSolvedInputMinLength &&
    problemSolved.length <= UtilApi.problemSolvedInputMaxLength;

  // Step 4 state
  const [duplicateResults, setDuplicateResults] = useState<DuplicateScanResultItem[]>([]);
  const [isStep4Valid, setIsStep4Valid] = useState(false);
  const [isDuplicateSearching, setIsDuplicateSearching] = useState(true);
  const contentIdRef = useRef<string | undefined>(undefined);
  const workflowIdRef = useRef<number | undefined>(undefined);
  const scanCompletedAtRef = useRef<number | undefined>(undefined);
  const stepEnteredAtRef = useRef(performance.now());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emitStepDuration = useCallback(() => {
    const durationMs = Math.round(performance.now() - stepEnteredAtRef.current);
    stepEnteredAtRef.current = performance.now();
    return durationMs;
  }, []);

  const getDecisionTimeMs = useCallback(() => {
    if (scanCompletedAtRef.current === undefined) return 0;
    return Math.round(performance.now() - scanCompletedAtRef.current);
  }, []);
  const flashbarMessageBroker = useMemo(() => FlashbarMessageBroker.create(), []);

  // Build Content for duplicate check
  const content = useMemo(() => {
    if (!contentType || !contentName) return undefined;
    const metadata = ContentMetadata.create(
      contentName,
      contentType,
      description,
      contentShortName || undefined,
      [], // tags
      problemSolved || undefined,
      proposal || undefined,
    );
    const defaultSubType = DefaultContentSubTypeMap.get(contentType);
    const isMigration =
      mainTechDomain === TechDomain.Migration || techDomains.includes(TechDomain.Migration);
    const contentSubType =
      contentType === ContentType.Pattern && !isMigration
        ? ContentSubType.PatternCloudNative
        : defaultSubType;
    const contentTypeMetadataObj = contentSubType
      ? (() => {
          const { templateId, templateVersion } = UtilApi.getCurrentTemplateInfo(contentSubType);
          return ContentTypeMetadata.create(templateId, templateVersion, contentSubType);
        })()
      : new ContentTypeMetadata();

    const templateMetadata: Record<string, unknown> = {};
    if (mainTechDomain) templateMetadata.mainTechDomain = mainTechDomain;
    if (techDomains.length > 0) templateMetadata.techDomains = techDomains;
    if (awsServices.length > 0) templateMetadata.awsServices = awsServices;
    if (workload.length > 0) templateMetadata.workload = workload;
    if (rType) templateMetadata.rType = rType;

    // contentData is nullable per the model's validation decorator, so undefined is safe here.
    return Content.create(
      metadata,
      contentTypeMetadataObj,
      Object.keys(templateMetadata).length > 0 ? templateMetadata : undefined,
      undefined as unknown as ContentNode,
    );
  }, [
    contentType,
    contentName,
    description,
    contentShortName,
    problemSolved,
    proposal,
    mainTechDomain,
    techDomains,
    awsServices,
    workload,
    rType,
  ]);

  const handleDuplicateCheckComplete = useCallback((results: DuplicateScanResultItem[]) => {
    setDuplicateResults(results);
  }, []);

  const handleValidChange = useCallback((valid: boolean) => {
    setIsStep4Valid(valid);
  }, []);

  const handleSearchingChange = useCallback((searching: boolean) => {
    setIsDuplicateSearching(searching);
    if (searching) {
      setIsStep4Valid(false);
    }
    if (!searching) {
      scanCompletedAtRef.current = performance.now();
    }
  }, []);

  const handleContentIdReceived = useCallback((id: string, wfId: number) => {
    contentIdRef.current = id;
    workflowIdRef.current = wfId;
  }, []);

  /**
   * Navigate to an external link (e.g. mailto:) while temporarily
   * suppressing the beforeunload guard so the browser doesn't show
   * a "leave page" dialog.
   */
  const navigateExternally = useCallback((url: string) => {
    suppressBeforeUnloadRef.current = true;
    window.location.href = url;
    // Re-enable after the browser has had a chance to fire beforeunload.
    // beforeunload fires after the current synchronous block but before
    // the next macrotask, so a zero-delay setTimeout is sufficient.
    setTimeout(() => {
      suppressBeforeUnloadRef.current = false;
    }, 0);
  }, []);

  const wizardSteps: WizardProps.Step[] = useMemo(
    () => [
      {
        title: constants.CREATE_CONTENT_WIZARD_STEP1_TITLE,
        isOptional: false,
        errorText:
          stepAttempted && !isStep1Valid ? constants.CREATE_CONTENT_WIZARD_STEP1_ERROR : undefined,
        content: (
          <Step1ContentTypeAudience
            contentType={contentType}
            setContentType={setContentType}
            contentAudience={contentAudience}
            setContentAudience={setContentAudience}
          />
        ),
      },
      {
        title: constants.CREATE_CONTENT_WIZARD_STEP2_TITLE,
        content: (
          <Step2ContentMetadata
            mainTechDomain={mainTechDomain}
            setMainTechDomain={setMainTechDomain}
            techDomains={techDomains}
            setTechDomains={setTechDomains}
            awsServices={awsServices}
            setAwsServices={setAwsServices}
            workload={workload}
            setWorkload={setWorkload}
            rType={rType}
            setRType={setRType}
            showValidationError={stepAttempted}
          />
        ),
      },
      {
        title: constants.CREATE_CONTENT_WIZARD_STEP3_TITLE,
        content: (
          <Step3ContentDetails
            contentName={contentName}
            setContentName={setContentName}
            contentShortName={contentShortName}
            setContentShortName={setContentShortName}
            description={description}
            setDescription={setDescription}
            proposal={proposal}
            setProposal={setProposal}
            problemSolved={problemSolved}
            setProblemSolved={setProblemSolved}
            showValidationError={stepAttempted}
            isSearchingDuplicates={isSearchingDuplicates}
            duplicateContents={duplicateContents}
            numberOfDuplicatesToShow={numberOfDuplicatesToShow}
            onShowMoreDuplicates={handleShowMoreDuplicates}
          />
        ),
      },
      {
        title: constants.CREATE_CONTENT_WIZARD_STEP4_TITLE,
        content: content ? (
          <Step4DuplicateCheck
            content={content}
            contentAudience={contentAudience!}
            onDuplicateCheckComplete={handleDuplicateCheckComplete}
            onValidChange={handleValidChange}
            onSearchingChange={handleSearchingChange}
            onContentIdReceived={handleContentIdReceived}
            contentId={contentIdRef.current}
            onBeforeExternalNavigation={navigateExternally}
          />
        ) : null,
      },
    ],
    [
      contentType,
      contentAudience,
      stepAttempted,
      isStep1Valid,
      mainTechDomain,
      techDomains,
      awsServices,
      workload,
      rType,
      contentName,
      contentShortName,
      description,
      proposal,
      problemSolved,
      isSearchingDuplicates,
      duplicateContents,
      numberOfDuplicatesToShow,
      handleShowMoreDuplicates,
      content,
      handleDuplicateCheckComplete,
      handleValidChange,
      handleSearchingChange,
      handleContentIdReceived,
      navigateExternally,
    ],
  );

  const [showCancelModal, setShowCancelModal] = useState(false);
  const unblockRef = useRef<(() => void) | null>(null);
  const blockedLocationRef = useRef<string | null>(null);
  const suppressBeforeUnloadRef = useRef(false);

  // Warn on browser close/refresh (native browser dialog)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (suppressBeforeUnloadRef.current) return;
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Intercept in-app navigation to show CancelWizardModal
  useEffect(() => {
    unblockRef.current = history.block((location) => {
      blockedLocationRef.current =
        location.pathname + (location.search || '') + (location.hash || '');
      setShowCancelModal(true);
      return false;
    });
    return () => unblockRef.current?.();
  }, [history]);

  const handleCancel = () => {
    blockedLocationRef.current = null; // cancel button → go home
    setShowCancelModal(true);
  };

  const handleCancelConfirm = () => {
    const durationMs = emitStepDuration();
    recordWizardStepNavigation({
      sourceStepIndex: activeStepIndex,
      destinationStepIndex: -1,
      action: 'CANCEL',
      durationMs,
    });
    if (activeStepIndex === 3 && contentIdRef.current && workflowIdRef.current !== undefined) {
      recordDuplicationScanAction({
        contentId: contentIdRef.current,
        workflowId: workflowIdRef.current,
        action: 'CANCEL',
        decisionTimeMs: getDecisionTimeMs(),
      });
    }
    const destination = blockedLocationRef.current || '/';
    unblockRef.current?.(); // release the navigation block
    unblockRef.current = null;
    setShowCancelModal(false);
    history.push(destination);
  };

  const handleSubmit = async () => {
    const currentContentId = contentIdRef.current;
    const currentWorkflowId = workflowIdRef.current;
    if (!isStep4Valid || !currentContentId || currentWorkflowId === undefined || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await createWorkflowEventWithStatePolling(
        currentContentId,
        currentWorkflowId,
        WorkflowEventTypes.PROPOSAL_APPROVE,
        '',
        undefined,
        undefined,
        undefined,
        { suppressSuccessFlashbar: true },
      );
      // Auto-switch to the author hat so the destination page renders as the author.
      if (isAutoChangeRoleEnabled) setActiveRole('author');
      // Emit telemetry only after successful submission to avoid
      // duplicate events and skewed duration measurements on retry.
      const durationMs = emitStepDuration();
      recordWizardStepNavigation({
        sourceStepIndex: activeStepIndex,
        destinationStepIndex: -1,
        action: 'SUBMIT',
        durationMs,
      });
      recordDuplicationScanAction({
        contentId: currentContentId,
        workflowId: currentWorkflowId,
        action: 'CREATE',
        decisionTimeMs: getDecisionTimeMs(),
      });
      // Release navigation guards before redirecting after successful submit
      unblockRef.current?.();
      unblockRef.current = null;
      // The redesigned editor route is only wired up when UxRedesign is on;
      // fall back to the legacy author view otherwise.
      history.push(
        isUxRedesignEnabled
          ? generatePath(RouterPaths.CONTENT_EDIT, { contentId: currentContentId })
          : `/content-viewer/author/${currentContentId}`,
      );
    } catch (error) {
      flashbarMessageBroker.pushErrorMessage(
        'Failed to submit content proposal. Please try again.',
      );
      setIsSubmitting(false);
    }
  };

  const handleNavigate = ({ detail }: { detail: { requestedStepIndex: number } }) => {
    // Emit metric when navigating backward from step 4 (duplicate check)
    if (
      activeStepIndex === 3 &&
      detail.requestedStepIndex < activeStepIndex &&
      contentIdRef.current &&
      workflowIdRef.current !== undefined
    ) {
      recordDuplicationScanAction({
        contentId: contentIdRef.current,
        workflowId: workflowIdRef.current,
        action: 'PREVIOUS',
        decisionTimeMs: getDecisionTimeMs(),
      });
    }
    // Only allow navigation forward if current step is valid
    if (detail.requestedStepIndex > activeStepIndex) {
      setStepAttempted(true);
      if (activeStepIndex === 0 && !isStep1Valid) {
        return; // Block navigation if step 1 is incomplete
      }
      if (activeStepIndex === 1 && !isStep2Valid) {
        return; // Block navigation if step 2 is incomplete
      }
      if (activeStepIndex === 2 && !isStep3Valid) {
        return; // Block navigation if step 3 is incomplete
      }
    }
    const action = detail.requestedStepIndex > activeStepIndex ? 'NEXT' : 'PREVIOUS';
    const durationMs = emitStepDuration();
    recordWizardStepNavigation({
      sourceStepIndex: activeStepIndex,
      destinationStepIndex: detail.requestedStepIndex,
      action,
      durationMs,
    });
    setStepAttempted(false);
    setActiveStepIndex(detail.requestedStepIndex);
  };

  const breadcrumbItems = [
    { text: constants.APP_NAME_BREAD_CRUMB, href: '/' },
    { text: constants.CREATE_CONTENT, href: '' },
  ];

  return (
    <SpaceBetween size="l">
      <Breadcrumbs items={breadcrumbItems} />
      <Wizard
        className="create-content-wizard"
        data-testid="create-content-wizard"
        i18nStrings={{
          stepNumberLabel: (stepNumber) => `Step ${stepNumber}`,
          collapsedStepsLabel: (stepNumber, stepsCount) => `Step ${stepNumber} of ${stepsCount}`,
          cancelButton: constants.CREATE_CONTENT_WIZARD_CANCEL_BUTTON,
          previousButton: constants.CREATE_CONTENT_WIZARD_PREVIOUS_BUTTON,
          nextButton: constants.CREATE_CONTENT_WIZARD_NEXT_BUTTON,
          submitButton: constants.CREATE_CONTENT_WIZARD_SUBMIT_BUTTON,
          optional: 'optional',
          submitButtonLoadingAnnouncement: 'Duplication scan in progress',
        }}
        isLoadingNextStep={isSubmitting}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        onNavigate={handleNavigate}
        activeStepIndex={activeStepIndex}
        steps={wizardSteps}
        customPrimaryActions={
          activeStepIndex === 3 ? (
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={handleCancel}
                data-testid="create-wizard-cancel-button"
              >
                {constants.CREATE_CONTENT_WIZARD_CANCEL_BUTTON}
              </Button>
              <Button
                variant="normal"
                disabled={isDuplicateSearching}
                onClick={() =>
                  handleNavigate({ detail: { requestedStepIndex: activeStepIndex - 1 } })
                }
              >
                {constants.CREATE_CONTENT_WIZARD_PREVIOUS_BUTTON}
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={isSubmitting}
                disabled={!isStep4Valid}
              >
                {constants.CREATE_CONTENT_WIZARD_SUBMIT_BUTTON}
              </Button>
            </SpaceBetween>
          ) : undefined
        }
      />
      <CancelWizardModal
        visible={showCancelModal}
        onDismiss={() => setShowCancelModal(false)}
        onConfirmCancel={handleCancelConfirm}
      />
    </SpaceBetween>
  );
};

export default CreateContentWizard;
